import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { toast } from 'react-toastify';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';


const EmployeeGoals = () => {
  const baseUrl = API_BASE_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const employeeId = location.state?.employeeId || id;
  const [fetchingEmployee, setFetchingEmployee] = useState(true);
  const [goals, setGoals] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [expandedTexts, setExpandedTexts] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [showDocPreview, setShowDocPreview] = useState(true);

  const getGoalDocumentUrl = (goal) => {
    if (!goal || !goal.goal_documents) return null;
    const docs = goal.goal_documents;
    let path = null;
    if (typeof docs === 'object') {
      path = docs.document || docs.file || docs.url;
    } else if (typeof docs === 'string') {
      path = docs;
    }

    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  const handleViewGoal = (goal) => {
    setSelectedGoal(goal);
    setShowDocPreview(true);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedGoal(null);
  };

  const toggleTextExpansion = (goalId, field) => {
    setExpandedTexts(prev => ({
      ...prev,
      [`${goalId}-${field}`]: !prev[`${goalId}-${field}`]
    }));
  };

  const renderTruncatedText = (text, goalId, field, maxLength = 55) => {
    const safeText = (text ?? '').toString();
    if (!safeText) return '';
    if (safeText.length <= maxLength) return safeText;

    const key = `${goalId || 'unknown'}-${field}`;
    const isExpanded = !!expandedTexts[key];
    const shownText = isExpanded ? safeText : `${safeText.slice(0, maxLength)}...`;

    return (
      <span>
        <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
        <button
          type="button"
          onClick={() => toggleTextExpansion(goalId || 'unknown', field)}
          className="btn btn-link btn-sm p-0 ms-2 text-decoration-none fw-semibold"
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </button>
      </span>
    );
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      setFetchingEmployee(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const emp = response.data.employee;
        setEmployee(emp);
        setGoals(emp.goals || []);
      } catch (err) {
        toast.error('Failed to fetch employee data.');
      } finally {
        setFetchingEmployee(false);
      }
    };
    if (employeeId) fetchEmployee();
  }, [baseUrl, employeeId]);

  const renderApprovalBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <span className="badge bg-success-subtle text-success border border-success-subtle">Approved</span>;
      case 'rejected':
        return <span className="badge bg-danger-subtle text-danger border border-danger-subtle">Rejected</span>;
      case 'pending':
        return (
          <span className="badge bg-warning-subtle text-warning border border-warning-subtle d-inline-flex align-items-center gap-1">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            Pending
          </span>
        );
      default:
        return <span className="badge bg-secondary-subtle text-secondary">{status || 'Unknown'}</span>;
    }
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Employee Key Performance Indicators</h4>
              <p className="text-muted mb-0 fs-13">View KPIs for this employee</p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary d-flex align-items-center gap-1"
              onClick={() => {
                if (typeof window !== 'undefined' && window.history) {
                  window.history.back();
                }
              }}
            >
              <i className="ti ti-arrow-left" /> Back to All Employees
            </button>
          </div>

          {/* Body Content */}
          {fetchingEmployee ? (
            <div className="card shadow-sm p-5 text-center">
              <div className="spinner-border text-primary mx-auto mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0 fs-13">Loading employee data...</p>
            </div>
          ) : employee ? (
            <>
              {/* Employee Summary Card */}
              <div className="card shadow-sm mb-4 bg-light border">
                <div className="card-body py-3 d-flex align-items-center flex-wrap gap-4">
                  <div>
                    <span className="text-muted fs-13 me-1">Name:</span>
                    <strong className="text-dark fs-15">{employee.first_name} {employee.last_name}</strong>
                  </div>
                  <div>
                    <span className="text-muted fs-13 me-1">Position:</span>
                    <span className="badge bg-white text-dark border fs-13">{employee.position}</span>
                  </div>
                </div>
              </div>

              {/* Goals Table Card */}
              <div className="card shadow-sm mb-4">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Activity</th>
                        <th>Stakeholder</th>
                        <th>KPI Metric</th>
                        <th style={{ width: '120px' }}>RAG</th>
                        <th>Quarter</th>
                        <th>Status</th>
                        <th className="text-center" style={{ width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {goals.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-5 text-muted">
                            <i className="ti ti-target-off fs-36 d-block mb-2 text-secondary opacity-50" />
                            No goals found for this employee.
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          const groupedByKra = goals.reduce((acc, goal) => {
                            const kraName =
                              (goal.key_result_area && typeof goal.key_result_area === 'object'
                                ? goal.key_result_area.name
                                : goal.key_result_area || goal.name) || 'Uncategorized';
                            if (!acc[kraName]) acc[kraName] = [];
                            acc[kraName].push(goal);
                            return acc;
                          }, {});

                          return Object.entries(groupedByKra).map(([kraName, kraGoals]) => (
                            <React.Fragment key={kraName}>
                              <tr className="bg-light fw-bold">
                                <td colSpan={7} className="py-2 text-dark">
                                  {(() => {
                                    const kraPercentage =
                                      kraGoals?.[0]?.key_result_area?.percentage ??
                                      kraGoals?.[0]?.kra?.percentage ??
                                      kraGoals?.[0]?.percentage;
                                    return (
                                      <>
                                        KRA: <span className="text-primary">{kraName}</span>
                                        <span className="badge bg-secondary ms-2 fw-semibold">
                                          Weight: {kraPercentage !== undefined && kraPercentage !== null ? `${kraPercentage}%` : '-'}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </td>
                              </tr>
                              {kraGoals.map((goal, idx) => (
                                <tr key={goal.id || `${kraName}-${idx}`}>
                                  <td>
                                    {renderTruncatedText(goal.activity, goal.id || `${kraName}-${idx}`, 'activity')}
                                  </td>
                                  <td>
                                    {renderTruncatedText(
                                      goal.stakeholder,
                                      goal.id || `${kraName}-${idx}`,
                                      'stakeholder',
                                      35
                                    )}
                                  </td>
                                  <td>{goal.metric}</td>
                                  <td>
                                    <span
                                      className="badge w-100 text-white"
                                      style={{ backgroundColor: goal.progress_indicator || '#6c757d' }}
                                    >
                                      {goal.rag_color || 'N/A'}
                                    </span>
                                  </td>
                                  <td>{goal.quarter}</td>
                                  <td>{renderApprovalBadge(goal.approval_status)}</td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-icon btn-light text-primary"
                                      title="View Goal Details"
                                      onClick={() => handleViewGoal(goal)}
                                    >
                                      <i className="ti ti-eye" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ));
                        })()
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="alert alert-danger shadow-sm" role="alert">
              <i className="ti ti-alert-circle me-2" />
              Employee not found.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>

      {/* Goal Details Modal */}
      {modalOpen && selectedGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">KPI Details</h5>
                <button type="button" className="btn-close" onClick={closeModal} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="table-responsive border rounded">
                  <table className="table table-striped mb-0">
                    <tbody>
                      <tr>
                        <th style={{ width: '35%' }} className="text-muted fw-semibold fs-14">Key Result Area:</th>
                        <td className="fw-medium text-dark">{selectedGoal.key_result_area?.name || selectedGoal.name}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Key Result Area Weight:</th>
                        <td className="fw-medium text-dark">{selectedGoal.key_result_area?.percentage || selectedGoal.percentage}%</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Activity:</th>
                        <td className="fw-medium text-dark" style={{ whiteSpace: 'pre-wrap' }}>{selectedGoal.activity}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">KPI Metric:</th>
                        <td className="fw-medium text-dark">{selectedGoal.metric}%</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Stakeholder:</th>
                        <td className="fw-medium text-dark">{selectedGoal.stakeholder}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Quarter:</th>
                        <td className="fw-medium text-dark">{selectedGoal.quarter}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">RAG Indicator:</th>
                        <td>
                          <span
                            className="badge text-white px-3 py-2"
                            style={{ backgroundColor: selectedGoal.progress_indicator || '#6c757d' }}
                          >
                            {selectedGoal.rag_color || 'N/A'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Completion Status:</th>
                        <td className="fw-medium text-dark">
                          {selectedGoal.status 
                            ? selectedGoal.status.charAt(0).toUpperCase() + selectedGoal.status.slice(1).replace(/_/g, ' ')
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Is Overdue:</th>
                        <td>
                          {selectedGoal.is_overdue ? (
                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle">Yes</span>
                          ) : (
                            <span className="badge bg-success-subtle text-success border border-success-subtle">No</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Approval Status:</th>
                        <td>{renderApprovalBadge(selectedGoal.approval_status)}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Due Date:</th>
                        <td className="fw-medium text-dark">
                          {selectedGoal.completion_date 
                            ? new Date(selectedGoal.completion_date).toLocaleDateString('en-GB')
                            : 'N/A'}
                        </td>
                      </tr>
                      {selectedGoal.approval_status === 'rejected' && selectedGoal.rejected_reason && (
                        <tr>
                          <th className="text-muted fw-semibold fs-14">Rejection Reason:</th>
                          <td className="text-danger fw-semibold">{selectedGoal.rejected_reason}</td>
                        </tr>
                      )}
                      {(() => {
                        const docUrl = getGoalDocumentUrl(selectedGoal);
                        if (!docUrl) return null;
                        const isImage = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(docUrl);

                        return (
                          <>
                            <tr>
                              <th className="text-muted fw-semibold fs-14">Attached Document:</th>
                              <td>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${showDocPreview ? 'btn-primary' : 'btn-outline-primary'} d-inline-flex align-items-center gap-1`}
                                    onClick={() => setShowDocPreview(!showDocPreview)}
                                  >
                                    <i className={`ti ${showDocPreview ? 'ti-eye-off' : 'ti-eye'}`} />
                                    {showDocPreview ? 'Hide Document Preview' : 'View Document On Site'}
                                  </button>
                                  <a
                                    href={docUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                                  >
                                    <i className="ti ti-external-link" /> Open in New Tab
                                  </a>
                                  <a
                                    href={docUrl}
                                    download
                                    className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                                  >
                                    <i className="ti ti-download" /> Download
                                  </a>
                                </div>
                              </td>
                            </tr>
                            {showDocPreview && (
                              <tr>
                                <td colSpan={2} className="p-0 border-0">
                                  <div className="p-3 bg-light border-top border-bottom rounded-bottom mt-2">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                      <span className="fw-semibold text-dark fs-14">
                                        <i className="ti ti-file-text me-1 text-primary" />
                                        Document Preview (On Site)
                                      </span>
                                      <span className="text-muted fs-12">{docUrl.split('/').pop()}</span>
                                    </div>
                                    {isImage ? (
                                      <div className="text-center p-2 bg-white rounded border">
                                        <img
                                          src={docUrl}
                                          alt="Goal Document"
                                          className="img-fluid rounded shadow-sm"
                                          style={{ maxHeight: '500px', objectFit: 'contain' }}
                                        />
                                      </div>
                                    ) : (
                                      <div className="ratio ratio-16x9 rounded border overflow-hidden shadow-sm" style={{ minHeight: '450px' }}>
                                        <iframe
                                          src={docUrl}
                                          title="Goal Document Preview"
                                          className="w-100 h-100 border-0"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeGoals;
